import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { Eye, EyeOff, AlertCircle } from 'lucide-react';
import { authService } from '../services/authService';

const LoginPage = () => {
  const navigate = useNavigate();
  const [showPassword, setShowPassword] = useState(false);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const handleLogin = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      // Authenticate with backend API
      const result = await authService.login(email, password);
      
      if (result.success) {
        navigate('/intelligence');
      } else {
        setError(result.error || 'Invalid email or password. Please try again.');
      }
    } catch (error) {
      setError('An error occurred during login. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#0B0B0C] text-white flex items-center justify-center p-4">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6 }}
        className="w-full max-w-md"
      >
        {/* Logo */}
        <div className="text-center mb-12">
          <img
            src="https://customer-assets.emergentagent.com/job_brand-architects-7/artifacts/s8bzyq96_IMG_9896.PNG"
            alt="ETERNITY"
            className="h-16 mx-auto mb-8"
          />
          
          <h1 className="text-3xl md:text-4xl font-bold text-[#EAEAEA] mb-3">
            Access the Eternity System
          </h1>
          <p className="text-sm text-[#9A9A9A]">
            Login to manage projects, tasks, reports, and workflows
          </p>
        </div>

        {/* Login Form */}
        <motion.form
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.2, duration: 0.6 }}
          onSubmit={handleLogin}
          className="bg-[#161316] border border-[#9A9A9A]/20 p-8"
        >
          {/* Email */}
          <div className="mb-6">
            <label className="block text-xs tracking-[0.1em] uppercase text-[#9A9A9A] mb-2">
              Email Address
            </label>
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="admin@eternitymp.com"
              className="w-full bg-[#0B0B0C] border border-[#9A9A9A]/30 text-[#EAEAEA] px-4 py-3 text-sm focus:border-[#E84A3D] focus:outline-none transition-colors duration-300"
              required
            />
          </div>

          {/* Password */}
          <div className="mb-6">
            <label className="block text-xs tracking-[0.1em] uppercase text-[#9A9A9A] mb-2">
              Password
            </label>
            <div className="relative">
              <input
                type={showPassword ? "text" : "password"}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="••••••••"
                className="w-full bg-[#0B0B0C] border border-[#9A9A9A]/30 text-[#EAEAEA] px-4 py-3 text-sm focus:border-[#E84A3D] focus:outline-none transition-colors duration-300"
                required
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-[#9A9A9A] hover:text-[#EAEAEA] transition-colors"
              >
                {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
              </button>
            </div>
          </div>

          {/* Remember & Forgot */}
          <div className="flex items-center justify-between mb-8">
            <label className="flex items-center gap-2 cursor-pointer">
              <input type="checkbox" className="w-4 h-4" />
              <span className="text-xs text-[#9A9A9A]">Remember me</span>
            </label>
            <a href="#" className="text-xs text-[#E84A3D] hover:text-[#FF5B4A] transition-colors">
              Forgot Password?
            </a>
          </div>

          {/* Submit Button */}
          <button
            type="submit"
            disabled={loading}
            className={`w-full bg-[#E84A3D] text-[#0B0B0C] py-3.5 text-xs tracking-[0.15em] uppercase font-medium hover:bg-[#FF5B4A] transition-all duration-300 ${
              loading ? 'opacity-50 cursor-not-allowed' : ''
            }`}
          >
            {loading ? 'Authenticating...' : 'Access Dashboard'}
          </button>

          {/* Error Message */}
          {error && (
            <motion.div
              initial={{ opacity: 0, y: -10 }}
              animate={{ opacity: 1, y: 0 }}
              className="mt-4 p-3 bg-[#FF5B4A]/10 border border-[#FF5B4A]/30 flex items-center gap-2"
            >
              <AlertCircle size={16} className="text-[#FF5B4A]" />
              <span className="text-xs text-[#FF5B4A]">{error}</span>
            </motion.div>
          )}
        </motion.form>

        {/* Back to Website */}
        <div className="text-center mt-8">
          <button
            onClick={() => navigate('/')}
            className="text-xs text-[#9A9A9A] hover:text-[#EAEAEA] transition-colors"
          >
            ← Back to Website
          </button>
        </div>
      </motion.div>
    </div>
  );
};

export default LoginPage;
